import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import ConfirmationDialog from "../components/ConfirmationDialog/ConfirmationDialog";
import { isLoggingOut } from "../utils/logoutGuard";

const ConfirmationContext = createContext(null);

let globalConfirmationHandler = null;

export const getConfirmation = (message, callback) => {
  // A confirmed logout already is the user's decision to leave - don't stack an
  // "Unsaved Changes" dialog on top of the login screen (see utils/logoutGuard).
  if (isLoggingOut()) {
    callback(true);
    return;
  }
  if (typeof globalConfirmationHandler === "function") {
    let options = {};
    try {
      if (typeof message === "string" && message.trim().startsWith("{")) {
        options = JSON.parse(message);
      } else {
        options = {
          title: "Unsaved Changes",
          message:
            typeof message === "string"
              ? message
              : "You have unsaved changes. Are you sure you want to leave without saving?",
          confirmLabel: "Discard & Leave",
          cancelLabel: "Stay",
          confirmColor: "#d32f2f",
        };
      }
    } catch (e) {
      options = {
        title: "Unsaved Changes",
        message:
          typeof message === "string"
            ? message
            : "You have unsaved changes. Are you sure you want to leave without saving?",
        confirmLabel: "Discard & Leave",
        cancelLabel: "Stay",
        confirmColor: "#d32f2f",
      };
    }

    if (!options.confirmLabel) options.confirmLabel = "Discard & Leave";
    if (!options.cancelLabel) options.cancelLabel = "Stay";
    if (!options.confirmColor) options.confirmColor = "#d32f2f";

    globalConfirmationHandler(options).then((confirmed) => {
      callback(Boolean(confirmed));
    });
  } else {
    const ok = window.confirm(
      typeof message === "string" ? message : "Leave page?"
    );
    callback(ok);
  }
};

export const ConfirmationProvider = ({ children }) => {
  const resolverRef = useRef(null);
  const [dialog, setDialog] = useState({ open: false });

  const confirm = useCallback(
    (options = {}) =>
      new Promise((resolve) => {
        resolverRef.current = resolve;
        setDialog({ open: true, ...options });
      }),
    []
  );

  useEffect(() => {
    globalConfirmationHandler = confirm;
    return () => {
      if (globalConfirmationHandler === confirm) {
        globalConfirmationHandler = null;
      }
    };
  }, [confirm]);

  const close = useCallback((confirmed) => {
    setDialog((current) => ({ ...current, open: false }));
    resolverRef.current?.(confirmed);
    resolverRef.current = null;
  }, []);

  return (
    <ConfirmationContext.Provider value={confirm}>
      {children}
      <ConfirmationDialog
        {...dialog}
        onConfirm={() => close(true)}
        onCancel={() => close(false)}
      />
    </ConfirmationContext.Provider>
  );
};

export const useConfirmation = () => {
  const confirm = useContext(ConfirmationContext);
  if (!confirm)
    throw new Error("useConfirmation must be used inside ConfirmationProvider");
  return confirm;
};
